import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { X } from 'lucide-react';
import * as React from 'react';

/**
 * A work page's decisions on the ticked rows (SR-456): drawn directly above the table only while
 * rows are ticked — "N selected", the page's actions, and *Clear*, which unticks them all.
 *
 * Where the page scrolls (`stickyTop`), it sticks that far under the window's top, and says its
 * height as `--selection-bar-height` on its parent, for the table's sticky header to sit under it.
 */
export function SelectionBar({
  count,
  onClear,
  stickyTop,
  children
}: {
  count: number;
  onClear: () => void;
  stickyTop?: string;
  children: React.ReactNode;
}) {
  const bar = React.useRef<HTMLDivElement>(null);
  const shown = count > 0;
  React.useLayoutEffect(() => {
    const box = bar.current;
    const parent = box?.parentElement;
    if (stickyTop === undefined || !box || !parent) return;
    const measure = () => parent.style.setProperty('--selection-bar-height', `${box.offsetHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => {
      observer.disconnect();
      parent.style.removeProperty('--selection-bar-height');
    };
  }, [stickyTop, shown]);
  if (!shown) return null;
  return (
    <div
      ref={bar}
      role="group"
      aria-label="The selected rows"
      data-selection-bar=""
      style={stickyTop !== undefined ? { top: stickyTop } : undefined}
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border border-border bg-[var(--color-slight-blue)] px-3 py-1.5 text-[0.8125rem]',
        stickyTop !== undefined && 'sticky z-10'
      )}
    >
      <span className="font-medium tabular-nums">{count} selected</span>
      {children}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="ml-auto gap-1"
        aria-label="Clear the selection"
        onClick={onClear}
      >
        <X className="size-3.5" aria-hidden />
        Clear
      </Button>
    </div>
  );
}
